import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Autocomplete,
  IconButton
} from '@mui/material';
import {
  EditNote as EditNoteIcon,
  DeleteOutline as DeleteOutlineIcon
} from '@mui/icons-material';

const CONTEXTO_DESCRIPCION_TEMPLATE =
  "Eres un asistente [tema]. Solo responde preguntas sobre [temas]. Si la pregunta no es relevante, indica amablemente que solo puedes responder sobre temas [tema].";

const AdministrarContextosDialog = ({
  open,
  onClose,
  contextOptions = [],
  setContextOptions,
  user,
  selectedContext,
  setSelectedContext,
  editingContext,
  setEditingContext,
  newContext,
  setNewContext,
  contextToDelete,
  setContextToDelete,
  iaLoading,
  setIaLoading
}) => {

  // Efectos
  useEffect(() => {
    if (open && !editingContext) {
      setNewContext({
        nombre: "",
        descripcion: CONTEXTO_DESCRIPCION_TEMPLATE
      });
    }
  }, [open, editingContext, setNewContext]);

  useEffect(() => {
    if (open) {
      fetch("/api/contextos/")
        .then(res => res.json())
        .then(setContextOptions);
    }
  }, [open, setContextOptions]);

  // Handlers
  const handleSugerirIA = async () => {
    setIaLoading(true);
    try {
      const res = await fetch("/api/contextos/sugerir_prompt/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nombre: newContext.nombre,
          descripcion: newContext.descripcion
        }),
      });
      const data = await res.json();
      if (data.prompt_sugerido) {
        setNewContext(c => ({ ...c, descripcion: data.prompt_sugerido }));
      } else {
        alert("No se pudo obtener sugerencia de la IA.");
      }
    } catch (err) {
      alert("Error al consultar la IA.");
    }
    setIaLoading(false);
  };

  const handleAgregarContexto = async () => {
    if (!newContext.nombre.trim() || !newContext.descripcion.trim()) return;
    
    await fetch("/api/contextos/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-User-Id": String(user.id)
      },
      body: JSON.stringify(newContext),
    });
    
    const res = await fetch("/api/contextos/");
    setContextOptions(await res.json());
    setNewContext({ nombre: "", descripcion: "" });
  };

  const handleEditarContexto = async () => {
    if (!newContext.nombre.trim()) return;
    
    await fetch(`/api/contextos/${editingContext.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-User-Id": user.id
      },
      body: JSON.stringify({
        nombre: newContext.nombre,
        descripcion: newContext.descripcion
      }),
    });
    
    const res = await fetch("/api/contextos/");
    setContextOptions(await res.json());
    setNewContext({ nombre: "", descripcion: "" });
    setEditingContext(null);
  };

  const handleEliminarContexto = async () => {
    await fetch(`/api/contextos/${contextToDelete.id}`, {
      method: "DELETE",
      headers: { "X-User-Id": user.id }
    });
    
    const res = await fetch("/api/contextos/");
    setContextOptions(await res.json());
    setContextToDelete(null);
  };

  const handleNombreChange = (e) => {
    const nombre = e.target.value
      .toLowerCase()
      .replace(/[^a-záéíóúüñ]/g, "");
    
    setNewContext(c => {
      let nuevaDescripcion = c.descripcion;
      if (!editingContext && (c.descripcion === CONTEXTO_DESCRIPCION_TEMPLATE || c.descripcion === "")) {
        nuevaDescripcion = CONTEXTO_DESCRIPCION_TEMPLATE
          .replace(/\[tema\]/g, nombre || "tema")
          .replace(/\[temas\]/g, nombre ? `${nombre}s` : "temas");
      }
      return {
        ...c,
        nombre,
        descripcion: nuevaDescripcion
      };
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minWidth: 720,
            maxWidth: 900,
            borderRadius: 3,
            background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
            boxShadow: "0 25px 80px rgba(99,102,241,0.2)",
            border: "1px solid rgba(99,102,241,0.1)"
          }
        }}
      >
        <DialogTitle sx={{ 
          background: "rgba(30,41,59,0.98)",
          color: "#fff",
          fontWeight: 800, 
          fontSize: 22,
          py: 2
        }}>
          Gestión de contextos
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {/* Selector de contexto */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 4, mt: 2 }}>
            <Autocomplete
              options={[...contextOptions].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }))}
              getOptionLabel={opt => opt.nombre}
              value={contextOptions.find(opt => opt.id === selectedContext) || null}
              onChange={(e, newValue) => {
                setSelectedContext(newValue ? newValue.id : "");
                if (editingContext) {
                  setNewContext({ nombre: "", descripcion: "" });
                }
              }}
              filterOptions={(options, { inputValue }) =>
                options.filter(opt =>
                  opt.nombre.toLowerCase().startsWith(inputValue.trim().toLowerCase())
                )
              }
             renderInput={params => (
                <TextField
                  {...params}
                  label="Buscar o seleccionar contexto"
                  size="small"
                  sx={{ 
                    minWidth: 260, 
                    maxWidth: 320,
                    '& .MuiInputBase-input::placeholder': {
                      fontSize: '13px',
                      color: 'rgba(30, 41, 59, 0.98)',
                      opacity: 1
                    },
                    '& .MuiAutocomplete-input::placeholder': {
                      fontSize: '13px', 
                      color: 'rgba(30, 41, 59, 0.98)',
                      opacity: 1
                    }
                  }}
                  InputLabelProps={{ sx: { fontWeight: 700, color: "#6366f1" } }}
                />
              )}
            />
          </Box>

          {/* Lista de contextos existentes */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ fontWeight: 700, color: "#334155", mb: 2, fontSize: 17 }}>
              Contextos existentes
            </Box>
            
            {contextOptions.length === 0 ? (
              <Box sx={{ color: "#6366f1", mt: 1, fontWeight: 500 }}>
                No hay contextos registrados.
              </Box>
            ) : (
              <Box sx={{
                border: "1px solid #e0e7ef",
                borderRadius: 2,
                background: "#fff",
                overflow: "hidden"
              }}>
                <Box sx={{
                  p: 2,
                  maxHeight: 300,
                  overflowY: "auto",
                  minWidth: 700,
                  maxWidth: "100%"
                }}>
                  {/* Cabecera */}
                  <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    fontWeight: 700,
                    color: "rgba(30,41,59,0.98)",
                    fontSize: 15,
                    borderBottom: "2px solid rgba(30,41,59,0.1)",
                    pb: 1,
                    mb: 1
                  }}>
                    <Box sx={{ width: 180, minWidth: 120, flexShrink: 0 }}>Nombre</Box>
                    <Box sx={{ width: 440, minWidth: 220, flexShrink: 0 }}>Prompt para NARA Inteligencia Artificial</Box>
                    <Box sx={{ width: 90, minWidth: 70, flexShrink: 0 }}>Acciones</Box>
                  </Box>
                  
                  {/* Filas de contextos */}
                  {(selectedContext
                    ? contextOptions.filter(ctx => ctx.id === selectedContext)
                    : contextOptions
                  ).map(ctx => (
                    <Box key={ctx.id} sx={{
                      display: "flex",
                      alignItems: "center",
                      borderBottom: "1px solid #e0e7ef",
                      py: 1,
                      px: 1,
                      fontSize: 14,
                      transition: "all 0.2s ease",
                      "&:hover": { 
                        background: "#f8fafc",
                        borderColor: "#6366f1"
                      },
                      "&:last-child": { borderBottom: "none" }
                    }}>
                      <Box sx={{
                        width: 180,
                        minWidth: 120,
                        flexShrink: 0,
                        color: "#6366f1",
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}>
                        {ctx.nombre}
                      </Box>
                      
                      <Box sx={{
                        width: 440,
                        minWidth: 220,
                        maxWidth: 440,
                        flexShrink: 0,
                        color: "#334155",
                        opacity: 0.8,
                        whiteSpace: "pre-line",
                        wordBreak: "break-word",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}>
                        {ctx.descripcion}
                      </Box>
                      
                      <Box sx={{
                        width: 90,
                        minWidth: 70,
                        flexShrink: 0,
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "flex-end"
                      }}>
                        {user?.empresa?.id === 4 && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingContext(ctx);
                                setNewContext({ 
                                  nombre: ctx.nombre, 
                                  descripcion: ctx.descripcion 
                                });
                              }}
                              title="Editar contexto"
                              sx={{ 
                                color: "#6366f1",
                                "&:hover": { background: "#6366f108" }
                              }}
                            >
                              <EditNoteIcon fontSize="small" />
                            </IconButton>
                            
                            <IconButton
                              size="small"
                              onClick={() => setContextToDelete(ctx)}
                              title="Eliminar contexto"
                              sx={{ 
                                color: "#ef4444",
                                "&:hover": { background: "#ef444408" }
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Formulario para crear/editar contexto */}
          {user?.empresa?.id === 4 && (
            <Box sx={{
              mb: 2,
              p: 3,
              borderRadius: 2,
              background: "#fff",
              border: "2px solid #6366f1",
              boxShadow: "0 8px 32px rgba(99,102,241,0.12)"
            }}>
              <Box sx={{ fontWeight: 700, color: "#6366f1", mb: 2, fontSize: 17 }}>
                {editingContext ? "✏️ Editar contexto" : "➕ Agregar nuevo contexto"}
              </Box>
              
              <TextField
                label="Nombre"
                value={newContext.nombre}
                onChange={handleNombreChange}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                inputProps={{ style: { textTransform: "lowercase" } }}
              />
          
              <TextField
                label="Descripción"
                value={newContext.descripcion}
                onChange={e => setNewContext(c => ({ ...c, descripcion: e.target.value }))}
                fullWidth
                size="small"
                multiline
                minRows={3}
                maxRows={6}
                sx={{ mb: 2 }}
              />

              {/* Botón Sugerir con IA */}
              <Button
                variant="outlined"
                color="primary"
                sx={{ mb: 2, textTransform: "none" }}
                disabled={iaLoading || !newContext.nombre.trim()}
                onClick={handleSugerirIA}
              >
                {iaLoading ? "Consultando IA..." : "Sugerir con IA"}
              </Button>
              
              {/* Botones de acción */}
              {!editingContext ? (
                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    sx={{ 
                      fontWeight: 700, 
                      fontSize: 14, 
                      minWidth: 120,
                      background: "linear-gradient(135deg, #10b981 0%, #16a34a 100%)",
                      textTransform: "none"
                    }}
                    onClick={handleAgregarContexto}
                    disabled={!newContext.nombre.trim() || !newContext.descripcion.trim()}
                  >
                    Agregar
                  </Button>
                  <Button
                    variant="outlined"
                    sx={{ fontWeight: 700, fontSize: 14, minWidth: 120, textTransform: "none" }}
                    onClick={() => setNewContext({ nombre: "", descripcion: "" })}
                    disabled={!newContext.nombre && !newContext.descripcion}
                  >
                    Cancelar
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    sx={{ 
                      fontWeight: 700, 
                      fontSize: 14, 
                      minWidth: 120,
                      background: "#f59e0b",
                      textTransform: "none",
                      "&:hover": { background: "#d97706" }
                    }}
                    onClick={handleEditarContexto}
                    disabled={!newContext.nombre.trim()}
                  >
                    Guardar cambios
                  </Button>
                  <Button
                    variant="outlined"
                    sx={{ fontWeight: 700, fontSize: 14, minWidth: 120, textTransform: "none" }}
                    onClick={() => {
                      setEditingContext(null);
                      setNewContext({ nombre: "", descripcion: "" });
                    }}
                  >
                    Cancelar
                  </Button>
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ color: "#64748b", fontWeight: 500, fontSize: 14, textAlign: "center", mt: 3 }}>
            Aquí podrás crear, editar y eliminar contextos globales para tus chatbots.
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2, background: "#f8fafc", borderTop: "1px solid #e0e7ef" }}>
          <Button 
            onClick={onClose}
            sx={{ 
              textTransform: "none",
              fontWeight: 600,
              color: "rgba(30,41,59,0.98)"
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación para eliminar contexto */}
      <Dialog 
        open={!!contextToDelete} 
        onClose={() => setContextToDelete(null)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 10px 40px rgba(0,0,0,0.15)"
          }
        }}
      >
        <DialogTitle sx={{ color: "#ef4444", fontWeight: 700 }}>
          ¿Eliminar contexto?
        </DialogTitle>
        <DialogContent>
          ¿Seguro que deseas eliminar el contexto <strong style={{ color: "#6366f1" }}>{contextToDelete?.nombre}</strong>?<br />
          Esta acción no se puede deshacer.
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setContextToDelete(null)} 
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleEliminarContexto}
            color="error"
            variant="contained"
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdministrarContextosDialog;